import type { InputFieldMetadata, InputFieldType, InputFieldValue } from "@charterindex/halyard";
import type { Editor } from "./editor";
import { bound, notify, observable } from "ecce-react";

/**
 * Default value used when no input data is provided.
 */
const DEFAULTS: Partial<{ [T in InputFieldType]: InputFieldValue<T> }> = {
	"Boolean": false,
};

/**
 * Manages the state of a single field of an {@link Editor}.
 *
 * Can be rendered into a Form using the `<EditorFieldControl>` component.
 */
export class EditorField<T extends InputFieldType> {
	readonly #editor: Editor<unknown>;

	readonly #metadata: InputFieldMetadata & { type: T };
	#initialValue: InputFieldValue<T> | null;
	#value: InputFieldValue<T> | null;

	constructor(editor: Editor<unknown>, metadata: InputFieldMetadata & { type: T }, initialValue: InputFieldValue<T>) {
		this.#editor = editor;
		this.#metadata = metadata;
		this.#initialValue = this.#value = EditorField.#getInitialValue(metadata, initialValue);
	}

	/**
	 * The name of the field in the Input.
	 */
	get name(): string {
		return this.#metadata.name;
	}

	/**
	 * The GraphQL Scalar type of the field.
	 */
	get type(): T {
		return this.#metadata.type;
	}

	/**
	 * Whether the field is required, or optional.
	 */
	get required(): boolean {
		return this.#metadata.required;
	}

	/**
	 * The current value of the field.
	 *
	 * Is @observable().
	 */
	@observable()
	get value(): InputFieldValue<T> | null {
		return this.#value;
	}

	/**
	 * Set the value of the field.
	 *
	 * Is @bound().
	 */
	@bound()
	setValue(nextValue: InputFieldValue<T> | null): void {
		if(nextValue === this.#value) {
			return;
		}

		this.#value = nextValue;
		notify(this, "value");
		this.#editor["handleFieldChanged"]();
	}

	/**
	 * Reset the value of the field to its initial value.
	 */
	resetValue(value: InputFieldValue<T>): void {
		this.#initialValue = EditorField.#getInitialValue(this.#metadata, value);
		this.setValue(this.#initialValue);
	}

	/**
	 * Test if the value of the field has been changed.
	 */
	get changed(): boolean {
		return this.#value !== this.#initialValue;
	}

	static #getInitialValue<T extends InputFieldType>(metadata: InputFieldMetadata & { type: T }, initialValue: InputFieldValue<T> | null) {
		return initialValue ?? DEFAULTS[metadata.type] ?? null;
	}
}
